import { Badge } from '@tempo-io/sui-badge';
import { cn } from '@tempo-io/sui-class-utils';
import { ExpandLess } from '@tempo-io/sui-icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { useState } from 'react';
import { SwimlaneCustomerChip } from '@/components/common/custom-field-value/CustomerFieldDisplay';
import type { LabelWithField } from '@/components/ui/swimlane/types';
import { useFlag } from '@/lib/hooks/feature-flags/useFlag';

export const DEFAULT_LABELS_TO_SHOW_STANDARD = 3;
export const DEFAULT_LABELS_TO_SHOW_COMPACT = 2;

export function Labels({
  labels = [],
  isCompact,
}: {
  labels?: LabelWithField[];
  isCompact?: boolean;
}) {
  const ideaManagerEnabled = useFlag('ideaManagerEnabled');
  const [collapsed, setCollapsed] = useState(true);
  const sanitizedLabels = labels.filter((label) => Boolean(label?.value));

  const defaultLabelsToShow = isCompact
    ? DEFAULT_LABELS_TO_SHOW_COMPACT
    : DEFAULT_LABELS_TO_SHOW_STANDARD;

  if (sanitizedLabels.length === 0) {
    return null;
  }

  return (
    <div
      className={cn('flex flex-wrap gap-2', {
        nowrap: collapsed,
        'flex-wrap': !collapsed,
        'max-w-[200px]': isCompact,
        'max-w-[300px]': !isCompact,
      })}
      data-testid="label-list"
    >
      {sanitizedLabels
        .slice(0, collapsed ? defaultLabelsToShow : sanitizedLabels.length)
        .map((label) => {
          const customerDisplayValues = ideaManagerEnabled
            ? (label.displayValues?.filter((displayValue) => displayValue.type === 'customer') ??
              [])
            : [];

          return (
            <Tooltip key={`${label.fieldName}:${label.value}`}>
              <TooltipTrigger asChild>
                <Badge
                  className={cn(
                    'text-ellipsis',
                    collapsed && 'flex-1 min-w-[1px] max-w-fit hover:w-full',
                  )}
                  data-testid="list-item-label"
                >
                  {customerDisplayValues.length > 0
                    ? customerDisplayValues.map((customer, index) => (
                        <SwimlaneCustomerChip
                          key={customer.id}
                          customer={customer}
                          compact
                          className={cn(index > 0 && 'ml-2')}
                        />
                      ))
                    : label.value}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>{label.fieldName}</TooltipContent>
            </Tooltip>
          );
        })}
      {sanitizedLabels.length > defaultLabelsToShow && (
        <Badge asChild>
          <button type="button" className="cursor-pointer" onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? (
              `+${sanitizedLabels.length - defaultLabelsToShow}`
            ) : (
              <ExpandLess size={16} />
            )}
          </button>
        </Badge>
      )}
    </div>
  );
}
